import * as React from 'react';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddBusinessRoundedIcon from '@mui/icons-material/AddBusinessRounded';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import AdminLayout, { AdminSection } from '../../components/AdminLayout';
import axiosInstance from '../../utils/axiosInstance';

const emptyFilters = {
  search: '',
  status: '',
  missing_admin: false,
  incomplete_profile: false,
};

const emptyCreateForm = {
  partner_code: '',
  display_name: '',
  legal_name: '',
  initial_admin_email: '',
  initial_admin_full_name: '',
  cmmc_compliance_level: '',
  government_entity: '',
};

function statusColor(status) {
  if (status === 'active') {
    return 'success';
  }
  if (status === 'suspended') {
    return 'warning';
  }
  return 'default';
}

function formatCount(value) {
  const numberValue = Number(value || 0);
  return Number.isFinite(numberValue) ? numberValue.toLocaleString() : '0';
}

function buildApiParams(filters) {
  return {
    search: filters.search || undefined,
    status: filters.status || undefined,
    missing_admin: filters.missing_admin ? 'true' : undefined,
    incomplete_profile: filters.incomplete_profile ? 'true' : undefined,
  };
}

function SummaryChip({ label, value }) {
  return (
    <Chip
      variant="outlined"
      size="small"
      label={`${label}: ${formatCount(value)}`}
      sx={{ maxWidth: '100%' }}
    />
  );
}

export default function AdminPartnersPage(props) {
  const location = useLocation();
  const [partners, setPartners] = useState([]);
  const [summary, setSummary] = useState({});
  const [filters, setFilters] = useState(emptyFilters);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');
  const [deletingPartner, setDeletingPartner] = useState(null);
  const [deleteSaving, setDeleteSaving] = useState(false);

  async function loadPartners(nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get('/admin/partners', {
        params: buildApiParams(nextFilters),
      });
      setPartners(Array.isArray(response.data?.partners) ? response.data.partners : []);
      setSummary(response.data?.summary || {});
    } catch (loadError) {
      setError(loadError?.response?.data?.message || 'Unable to load partner registry.');
      setPartners([]);
      setSummary({});
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPartners(emptyFilters);
  }, []);

  useEffect(() => {
    if (location.state?.feedback) {
      setCreateSuccess(location.state.feedback);
    }
  }, [location.state]);

  function updateFilter(field, value) {
    setFilters((current) => ({ ...current, [field]: value }));
  }

  function applyFilters(event) {
    event.preventDefault();
    loadPartners(filters);
  }

  function resetFilters() {
    setFilters(emptyFilters);
    loadPartners(emptyFilters);
  }

  function openCreateDialog() {
    setCreateForm(emptyCreateForm);
    setCreateError('');
    setCreateSuccess('');
    setCreateOpen(true);
  }

  function closeCreateDialog() {
    if (createSaving) {
      return;
    }
    setCreateOpen(false);
  }

  function updateCreateForm(field, value) {
    setCreateError('');
    setCreateForm((current) => ({ ...current, [field]: value }));
  }

  async function handleCreatePartner(event) {
    event.preventDefault();
    setCreateSaving(true);
    setCreateError('');
    setCreateSuccess('');
    try {
      const response = await axiosInstance.post('/admin/partners', {
        partner_code: createForm.partner_code,
        display_name: createForm.display_name,
        legal_name: createForm.legal_name,
        initial_admin_email: createForm.initial_admin_email,
        initial_admin_full_name: createForm.initial_admin_full_name,
        cmmc_compliance_level: createForm.cmmc_compliance_level || null,
        government_entity: createForm.government_entity === '' ? null : createForm.government_entity === 'true',
      });
      const partnerName = response.data?.partner?.display_name || createForm.display_name;
      const inviteEmail = response.data?.invite?.email || createForm.initial_admin_email;
      setCreateSuccess(`${partnerName} created. Initial admin invite is pending for ${inviteEmail}.`);
      setCreateForm(emptyCreateForm);
      setCreateOpen(false);
      await loadPartners(filters);
    } catch (createRequestError) {
      setCreateError(createRequestError?.response?.data?.message || 'Unable to create parent partner.');
    } finally {
      setCreateSaving(false);
    }
  }

  function openDeletePartnerDialog(partner) {
    setDeletingPartner(partner);
    setError('');
    setCreateSuccess('');
  }

  function closeDeletePartnerDialog() {
    if (!deleteSaving) {
      setDeletingPartner(null);
    }
  }

  async function handleDeletePartner() {
    if (!deletingPartner) {
      return;
    }
    const label = deletingPartner.display_name || deletingPartner.partner_code || 'Partner';
    setDeleteSaving(true);
    setError('');
    setCreateSuccess('');
    try {
      await axiosInstance.delete(`/admin/partners/${deletingPartner.partner_id}`);
      setCreateSuccess(`${label} deleted.`);
      setDeletingPartner(null);
      await loadPartners(filters);
    } catch (deleteError) {
      setError(deleteError?.response?.data?.message || 'Unable to delete partner.');
    } finally {
      setDeleteSaving(false);
    }
  }

  return (
    <AdminLayout
      props={props}
      breadcrumbItems={[{ label: 'AMX xConsole', href: '/' }, { label: 'Admin Dashboard', href: '/admin' }, { label: 'Partner Registry' }]}
      title="Partner Registry"
      subtitle="Review parent partner accounts, business-unit hierarchy, admin ownership, and fleet counts."
    >
      <AdminSection
        title="Registry Filters"
        subtitle="Narrow the partner registry by account state, profile readiness, and hierarchy scope."
        action={
          <Button variant="contained" startIcon={<AddBusinessRoundedIcon />} onClick={openCreateDialog}>
            Create Partner
          </Button>
        }
      >
        <Stack component="form" spacing={2} onSubmit={applyFilters}>
          {createSuccess ? <Alert severity="success">{createSuccess}</Alert> : null}
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', lg: 'center' }}>
            <TextField
              label="Partner Code or Name"
              value={filters.search}
              onChange={(event) => updateFilter('search', event.target.value)}
              size="small"
              sx={{ minWidth: { lg: 280 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Status"
              value={filters.status}
              onChange={(event) => updateFilter('status', event.target.value)}
              size="small"
              sx={{ minWidth: { lg: 180 } }}
            >
              <MenuItem value="">All statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.missing_admin}
                  onChange={(event) => updateFilter('missing_admin', event.target.checked)}
                />
              }
              label="Missing Admin"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.incomplete_profile}
                  onChange={(event) => updateFilter('incomplete_profile', event.target.checked)}
                />
              }
              label="Incomplete Profile"
            />
            <Stack direction="row" spacing={1} justifyContent={{ xs: 'stretch', lg: 'flex-end' }} sx={{ flexGrow: 1 }}>
              <Button type="submit" variant="contained" startIcon={<SearchRoundedIcon />}>
                Apply
              </Button>
              <Button type="button" variant="outlined" startIcon={<RestartAltRoundedIcon />} onClick={resetFilters}>
                Reset
              </Button>
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <SummaryChip label="Rows" value={summary.total} />
            <SummaryChip label="Partners" value={summary.partners} />
            <SummaryChip label="Business Units" value={summary.business_units} />
            <SummaryChip label="Missing Admin" value={summary.missing_admin} />
            <SummaryChip label="Incomplete Profiles" value={summary.incomplete_profile} />
          </Stack>
        </Stack>
      </AdminSection>

      <AdminSection title="Partner Accounts" subtitle="Counts roll up across each partner account and its business units.">
        {loading ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={24} />
            <span>Loading partner registry...</span>
          </Stack>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1180 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Partner Name</TableCell>
                  <TableCell>Partner Code</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Business Units</TableCell>
                  <TableCell align="right">Users</TableCell>
                  <TableCell align="right">Admins</TableCell>
                  <TableCell align="right">Gateways</TableCell>
                  <TableCell>Main Admin Account Email Address</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {partners.length ? partners.map((partner) => (
                    <TableRow key={partner.partner_id} hover>
                      <TableCell sx={{ minWidth: 240 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                          {partner.display_name || partner.partner_code}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, wordBreak: 'break-word' }}>
                          Partner account
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', minWidth: 150 }}>{partner.partner_code}</TableCell>
                      <TableCell>
                        <Chip size="small" label={partner.status || 'active'} color={statusColor(partner.status)} />
                      </TableCell>
                      <TableCell align="right">{formatCount(partner.business_unit_count)}</TableCell>
                      <TableCell align="right">{formatCount(partner.user_count)}</TableCell>
                      <TableCell align="right">{formatCount(partner.admin_count)}</TableCell>
                      <TableCell align="right">{formatCount(partner.gateway_count)}</TableCell>
                      <TableCell sx={{ minWidth: 260, wordBreak: 'break-all' }}>
                        {partner.main_admin_account_email || (
                          <Chip size="small" label="Missing" color="warning" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Button
                            component={RouterLink}
                            to={`/admin/partners/${partner.partner_id}`}
                            variant="outlined"
                            size="small"
                            startIcon={<OpenInNewRoundedIcon />}
                          >
                            Open
                          </Button>
                          <Tooltip title="Delete partner account">
                            <IconButton size="small" color="error" onClick={() => openDeletePartnerDialog(partner)}>
                              <DeleteForeverRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography variant="body2" color="text.secondary">
                        No partners match the selected filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </AdminSection>

      <Dialog open={createOpen} onClose={closeCreateDialog} fullWidth maxWidth="sm">
        <DialogTitle>Create Partner</DialogTitle>
        <Box component="form" onSubmit={handleCreatePartner}>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Partner Code"
                value={createForm.partner_code}
                onChange={(event) => updateCreateForm('partner_code', event.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Display Name"
                value={createForm.display_name}
                onChange={(event) => updateCreateForm('display_name', event.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Legal Name"
                value={createForm.legal_name}
                onChange={(event) => updateCreateForm('legal_name', event.target.value)}
                required
                fullWidth
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Initial Partner Admin Email"
                  type="email"
                  value={createForm.initial_admin_email}
                  onChange={(event) => updateCreateForm('initial_admin_email', event.target.value)}
                  required
                  fullWidth
                />
                <TextField
                  label="Initial Partner Admin Full Name"
                  value={createForm.initial_admin_full_name}
                  onChange={(event) => updateCreateForm('initial_admin_full_name', event.target.value)}
                  required
                  fullWidth
                />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select
                  label="CMMC Compliance Level"
                  value={createForm.cmmc_compliance_level}
                  onChange={(event) => updateCreateForm('cmmc_compliance_level', event.target.value)}
                  fullWidth
                >
                  <MenuItem value="">Not specified</MenuItem>
                  <MenuItem value="level_1">Level 1</MenuItem>
                  <MenuItem value="level_2">Level 2</MenuItem>
                  <MenuItem value="level_3">Level 3</MenuItem>
                </TextField>
                <TextField
                  select
                  label="Government Entity"
                  value={createForm.government_entity}
                  onChange={(event) => updateCreateForm('government_entity', event.target.value)}
                  required
                  fullWidth
                >
                  <MenuItem value="">Select yes or no</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
              </Stack>
              {createError ? <Alert severity="error">{createError}</Alert> : null}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button type="button" onClick={closeCreateDialog} disabled={createSaving}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={createSaving}>
              {createSaving ? 'Creating...' : 'Create and Invite'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(deletingPartner)} onClose={closeDeletePartnerDialog} fullWidth maxWidth="sm">
        <DialogTitle>Delete Partner Account</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                {deletingPartner?.display_name || deletingPartner?.partner_code}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {deletingPartner?.partner_code}
              </Typography>
            </Box>
            <Alert severity="error">
              This permanently deletes the partner account, its business units, and every AMX user under the account.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeletePartnerDialog} disabled={deleteSaving}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeletePartner} disabled={deleteSaving}>
            {deleteSaving ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
}
